// 방문자 Table Columns
import moment from "moment";
import { Cell, Row } from "react-table";
import { ApprovalStatusMap, ContractVisitorListModel, VisitStatusMap } from "src/api/visitor/visitor-types";
import { BaseButton, BaseTooltip } from "src/components";
import { useModal } from "src/recoil/modalState/hook";
import { ViewYmdFormat } from "src/utils";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import CsnkeyModal from "../../components/CsnkeyModal";
import useCsnkey from "../../hooks/useCsnkey";
import { formatPhoneNumber } from "src/utils/common-util";
import { formatVisitorMobileNumber } from "../../utils";
import ApprovalStatusTag from "./ApprovalStatusTag";
import { VisitorDateCell } from "./VisitorDateCell";
import { VisitorLinkCell } from "./VisitorLinkCell";
import VisitStatusTag from "./VisitStatusTag";
import VisitStatusTagV2, { getVisitStatusV2Label } from "./VisitStatusTagV2";
import DetailLink from "src/components/DetailLink";
import ContractManageIdLinkCell from "src/components/contract/ContractManageIdLinkCell";
import { PagePath } from "src/pages/product/details";
import { getParkingStatus } from "../../visitor-types";

// 방문 목적 표시 포맷
// - enum 코드 기반 (WORK_MEETING / FRIEND_VISIT / PACKAGE_DELIVERY / FOOD_DELIVERY): 한글 라벨 노출
// - ETC: "기타 ({visitPurposeDescription})", description 없으면 "기타 (-)"
const VISIT_PURPOSE_LABELS: Record<string, string> = {
  WORK_MEETING: "업무 미팅",
  FRIEND_VISIT: "지인 방문",
  PACKAGE_DELIVERY: "퀵/택배",
  FOOD_DELIVERY: "음식 배달",
};
export const formatVisitPurpose = (value?: string | null, description?: string | null): string => {
  if (!value) return "-";
  if (value === "ETC") return `기타 (${description || "-"})`;
  return VISIT_PURPOSE_LABELS[value] ?? value;
};

const INVITATION_CHANNEL_LABELS: Record<string, string> = {
  MOBILE_APP: "Mobile App",
  CTRL_ROOM: "Ctrl.room",
  TAAP_SPACE: "TaapSpace",
  SELF_REGIST: "Self Registration",
};
const formatInvitationChannel = (value?: string | null): string => {
  if (!value) return "-";
  return INVITATION_CHANNEL_LABELS[value] ?? value;
};

export const columns = [
  {
    Header: "방문신청번호",
    accessor: "visitApplyNumber",
    width: 120,
    sticky: "left",
    Cell: (props: Cell<ContractVisitorListModel>) => <VisitorLinkCell {...props} />,
  },
  {
    Header: "방문자 ID",
    accessor: "contractVisitorId",
    sticky: "left",
    width: 120,
    Cell: (props: Cell<ContractVisitorListModel>) => {
      return <div className="">{props.value ?? "-"}</div>;
    },
  },
  {
    Header: "이용자 승인상태",
    accessor: "memberApprovalStatus",
    sticky: "left",
    width: 130,
    Cell: ({ value }: Cell<ContractVisitorListModel>) => {
      return <div className="flex-center-center w-100">{value ? <ApprovalStatusTag value={value} /> : "-"}</div>;
    },
    excelValue: (row: ContractVisitorListModel) => {
      return row.memberApprovalStatus ? ApprovalStatusMap[row.memberApprovalStatus as keyof typeof ApprovalStatusMap] : "-";
    },
  },
  {
    Header: "이용자 마스터 승인상태",
    accessor: "tenantManagerApprovalStatus",
    sticky: "left",
    width: 130,
    Cell: ({ value }: Cell<ContractVisitorListModel>) => {
      return <div className="flex-center-center w-100">{value ? <ApprovalStatusTag value={value} /> : "-"}</div>;
    },
    excelValue: (row: ContractVisitorListModel) => {
      return row.tenantManagerApprovalStatus ? ApprovalStatusMap[row.tenantManagerApprovalStatus as keyof typeof ApprovalStatusMap] : "-";
    },
  },
  {
    Header: "관리자 승인상태",
    accessor: "approvalStatus",
    sticky: "left",
    width: 130,
    Cell: ({ value }: Cell<ContractVisitorListModel>) => {
      return <div className="flex-center-center w-100">{value ? <ApprovalStatusTag value={value} /> : "-"}</div>;
    },
    excelValue: (row: ContractVisitorListModel) => {
      return row.approvalStatus ? ApprovalStatusMap[row.approvalStatus as keyof typeof ApprovalStatusMap] : "-";
    },
  },
  {
    Header: "방문상태",
    accessor: "visitStatus",
    sticky: "left",
    width: 160,
    Cell: ({ value, row }: Cell<ContractVisitorListModel>) => {
      return (
        <div className="flex-center-center w-100 gap10">
          {row.original.isDeleted ? <VisitStatusTag value="VISIT_DELETE" /> : <VisitStatusTag value={value || "-"} />}
          <VisitStatusTagV2 visitStatus={value} accessUpdatedAt={row.original.accessUpdatedAt} isDeleted={row.original.isDeleted} />
        </div>
      );
    },
    excelValue: (row: ContractVisitorListModel) => {
      return getVisitStatusV2Label({
        visitStatus: row.visitStatus,
        accessUpdatedAt: row.accessUpdatedAt,
        isDeleted: row.isDeleted,
      });
    },
  },

  {
    Header: "방문자명",
    accessor: "visitorName",
    width: 200,
    Cell: (props: Cell<ContractVisitorListModel>) => {
      return (
        <div className="flex-center-start w-100">
          <BaseTooltip contents={props.value || "-"} isSingleLine={true} />
        </div>
      );
    },
  },
  {
    Header: "방문자 휴대폰 번호",
    accessor: "visitorMobileNumber",
    width: 120,
    Cell: ({ value }: Cell<ContractVisitorListModel>) => {
      const phoneNumber = formatVisitorMobileNumber(value);
      return <div className="flex-center-start w-100">{phoneNumber || "-"}</div>;
    },
    excelValue: (row: ContractVisitorListModel) => {
      const phoneNumber = formatVisitorMobileNumber(row.visitorMobileNumber || "");
      return phoneNumber || "-";
    },
  },
  {
    Header: "방문자 이메일",
    accessor: "visitorEmail",
    width: 160,
    Cell: ({ row, value }: Cell<ContractVisitorListModel>) => {
      if (row.original.visitorEmail) {
        return (
          <div className="flex-center-start w-100">
            <BaseTooltip
              contents={`${row.original.visitorEmail} ${row.original.visitorEmailLang === "en" ? "(영문)" : "(국문)"}`}
              isSingleLine={true}
            />
          </div>
        );
      }

      return <div className="flex-center-start w-100">{"-"}</div>;
    },
    excelValue: (row: ContractVisitorListModel) => {
      return row.visitorEmail ? `${row.visitorEmail} ${row.visitorEmailLang === "en" ? "(영문)" : "(국문)"}` : "-";
    },
  },
  {
    Header: "방문자 소속회사",
    accessor: "organizationName",
    width: 160,
    Cell: ({ value }: Cell<ContractVisitorListModel>) => {
      if (value) {
        return (
          <div className="flex-center-start w-100">
            <BaseTooltip contents={value} isSingleLine={true} />
          </div>
        );
      }
      return <div className="flex-center-start w-100">{"-"}</div>;
    },
    excelValue: (row: ContractVisitorListModel) => row.organizationName || "-",
  },
  {
    Header: "방문 목적",
    accessor: "visitPurpose",
    width: 140,
    Cell: ({ value, row }: Cell<ContractVisitorListModel>) => {
      const display = formatVisitPurpose(value, row.original.visitPurposeDescription);
      if (display === "-") {
        return <div className="flex-center-center w-100">{"-"}</div>;
      }
      if (value === "ETC") {
        return (
          <div className="flex-center-center w-100">
            <BaseTooltip contents={display} className="text-center w-100" />
          </div>
        );
      }
      return <div className="flex-center-center w-100">{display}</div>;
    },
    excelValue: (row: ContractVisitorListModel) => formatVisitPurpose(row.visitPurpose, row.visitPurposeDescription),
  },
  {
    Header: "방문 시작일시",
    accessor: "visitStartTime",
    width: 160,
    Cell: (props: Cell<ContractVisitorListModel>) => (
      <p className="w-100 text-center">{props.value ? moment(props.value).format(ViewYmdFormat.FULL) : "-"}</p>
    ),
    excelValue: (row: ContractVisitorListModel) => {
      return row.visitStartTime ? moment(row.visitStartTime).format(ViewYmdFormat.FULL) : "-";
    },
  },
  {
    Header: "방문 종료일시",
    accessor: "visitEndTime",
    width: 160,
    Cell: (props: Cell<ContractVisitorListModel>) => (
      <p className="w-100 text-center">{props.value ? moment(props.value).format(ViewYmdFormat.FULL) : "-"}</p>
    ),
    excelValue: (row: ContractVisitorListModel) => {
      return row.visitEndTime ? moment(row.visitEndTime).format(ViewYmdFormat.FULL) : "-";
    },
  },
];

export const parkingColumns = [
  {
    Header: () => (
      <div className="d-flex flex-center">
        <span>주차 차량 번호</span>
        <BaseTooltip touchIcon={"QUESTION"} size={16} className="ml4">
          할인 자동 적용 조건
          <ul className="base-list">
            <li className="font13 text-primary">
              주차시스템 연동 활성화 <br />
              (미연동 시, 입력된 차량번호는 주차 할인 혜택이 자동 적용되지 않습니다.)
            </li>
            <li className="font13 text-primary">주차시스템 할인 설정 완료 </li>
            <li className="font13 text-primary">정확한 차량번호 입력</li>
          </ul>
        </BaseTooltip>
      </div>
    ),
    accessor: "visitorCarNumber",
    width: 200,
    Cell: ({ row, value }: Cell<ContractVisitorListModel>) => {
      const { preDiscountId, parkingVendorCode } = row.original;

      const parkingStatus = getParkingStatus(parkingVendorCode || "", preDiscountId || "");

      return (
        <div className="d-flex w-100 gap8 flex-center">
          <BaseTooltip contents={value || "-"} isSingleLine={true} />
          {value && (
            <div className={parkingStatus.className}>
              <p>{parkingStatus.label}</p>
            </div>
          )}
        </div>
      );
    },
  },
];

export const afterColumns = [
  {
    // 상세 페이지 키카드할당
    Header: "키카드 할당",
    accessor: "visitorKeyCardButton",
    width: 105,

    Cell: ({ row }: Cell<ContractVisitorListModel>) => {
      const { setAbstractModalZ1 } = useModal();
      const { isAuthority } = usePartnerAuthority();

      const isDeleted = row?.original?.isDeleted;
      const visitStatus = row?.original.visitStatus;

      // BASIC w 권한이 없으면 할당 버튼 숨김
      if (!isAuthority("w", "BASIC")) return <span>-</span>;

      // 방문자가 삭제되었거나 방문상태가 삭제/종료 경우 할당 불가
      const isDisabled = isDeleted || visitStatus === "VISIT_DELETE" || visitStatus === "VISIT_CLOSE";

      return (
        <div className="w-100 flex-center-center">
          <BaseButton
            disabled={isDisabled}
            title="할당"
            className="color-white"
            onClick={() =>
              setAbstractModalZ1({
                isOpen: true,
                children: <CsnkeyModal contractVisitor={row?.original as any} />,
              })
            }
          />
        </div>
      );
    },
  },
  {
    Header: "키카드 번호",
    accessor: "visitorKeyCard",
    width: 164,
    Cell: ({ row, data }: { row: Row<ContractVisitorListModel>; data: ContractVisitorListModel[] }) => {
      const { setBaseModal } = useModal();
      const { isAuthority } = usePartnerAuthority();
      //목록에 모든 키번호로 조회
      const contractVisitorIds = data.map((item) => item.contractVisitorId?.toString() || "");

      const contractVisitorId = row.original?.contractVisitorId?.toString() || "";

      const { deleteCardNumber, csnkeyList } = useCsnkey(contractVisitorIds);

      const cardNumber = csnkeyList?.data?.data?.content?.find((item) => String(item.contractVisitorId) === String(contractVisitorId))?.accessToken;

      return (
        <>
          {cardNumber ? (
            <div className="w-100 flex-row flex-start">
              <BaseTooltip className="" contents={cardNumber || "-"} isSingleLine={true}></BaseTooltip>
              {isAuthority("d", "BASIC") && (
                <div
                  className="delete-btn-x shrink-0"
                  onClick={() =>
                    setBaseModal({
                      isOpen: true,
                      title: "키카드 번호를 삭제하시겠습니까?",
                      children: "삭제하면 입력되어 있는 번호가 삭제됩니다.",
                      btnRightTitle: "확인",
                      btnLeftTitle: "취소",
                      onClick: () => {
                        deleteCardNumber(
                          { contractVisitorId: contractVisitorId },
                          {
                            onSuccess: () => {
                              setBaseModal({ isOpen: false });
                            },
                          },
                        );
                      },
                    })
                  }
                ></div>
              )}
            </div>
          ) : (
            <div className="w-100 flex-row flex-start">
              <span>-</span>
            </div>
          )}
        </>
      );
    },
    excelValue: (row: ContractVisitorListModel) => {
      return row.visitorKeyCard ? row.visitorKeyCard : "-";
    },
  },

  {
    Header: "초대일시",
    accessor: "invitationDate",
    width: 160,
    Cell: (props: Cell<ContractVisitorListModel>) => (
      <p className="w-100 text-center">{props.value ? moment(props.value).format(ViewYmdFormat.FULL) : "-"}</p>
    ),
    excelValue: (row: ContractVisitorListModel) => {
      return row.invitationDate ? moment(row.invitationDate).format(ViewYmdFormat.FULL) : "-";
    },
  },
  {
    Header: "초대자 이메일",
    accessor: "inviteeMemberId",
    width: 160,
    Cell: (props: Cell<ContractVisitorListModel>) => {
      return (
        <div className="flex-center-start w-100">
          <BaseTooltip contents={props.value || "-"} />
        </div>
      );
    },
  },
  {
    Header: "초대자 회원번호",
    accessor: "inviteeMemberNo",
    width: 120,
    Cell: (props: Cell<ContractVisitorListModel>) => {
      return (
        <div className="flex-center-center w-100">
          <BaseTooltip contents={props.value || "-"} className="text-left w-100" />
        </div>
      );
    },
  },
  {
    // 신청번호(난수) 대체 컬럼
    Header: "계약관리 ID",
    accessor: "contractManageId",
    width: 120,
    Cell: ({ data, row }: any) => (
      <ContractManageIdLinkCell
        contractManageId={row.original.contractManageId}
        contractManageIdsInPage={data.map((item: ContractVisitorListModel) => item.contractManageId)}
      />
    ),
  },
  {
    Header: "초대경로",
    accessor: "invitationChannel",
    width: 130,
    Cell: (props: Cell<ContractVisitorListModel>) => {
      return (
        <div className="flex-center-center w-100">
          <BaseTooltip contents={formatInvitationChannel(props.value)} className="text-center w-100" />
        </div>
      );
    },
    excelValue: (row: ContractVisitorListModel) => {
      return formatInvitationChannel(row.invitationChannel);
    },
  },
  {
    Header: "최근 출입일시",
    accessor: "accessUpdatedAt",
    width: 160,
    Cell: (props: Cell<ContractVisitorListModel>) => {
      return (
        <div className="w-100">
          <p>{props.value ? moment(props.value).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM) : "-"}</p>
        </div>
      );
    },
    excelValue: (row: ContractVisitorListModel) => {
      return row.accessUpdatedAt ? moment(row.accessUpdatedAt).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM) : "-";
    },
  },
  {
    Header: "최종 수정일시",
    accessor: "modifiedDate",
    width: 160,
    Cell: (props: Cell<ContractVisitorListModel>) => (
      <div className="w-100">
        <p className="text-center">{props.value ? moment(props.value).format(ViewYmdFormat.FULL) : "-"}</p>
      </div>
    ),
    excelValue: (row: ContractVisitorListModel) => {
      return row.modifiedDate ? moment(row.modifiedDate).format(ViewYmdFormat.FULL) : "-";
    },
  },
];

// 엑셀 다운로드 전용 컬럼 - 방문 처리상태 (방문상태 컬럼 앞에 삽입, VisitStatusTag v1 라벨)
export const excelVisitProcessStatusColumn = {
  Header: "방문 처리상태",
  accessor: "visitProcessStatus",
  excelValue: (row: ContractVisitorListModel) => {
    return row.isDeleted ? "취소" : VisitStatusMap[row.visitStatus as keyof typeof VisitStatusMap] || "-";
  },
};

// 엑셀 다운로드 전용 컬럼 (회원번호와 계약관리 ID 사이에 위치)
export const excelOnlyColumns = [
  {
    Header: "초대자 닉네임",
    accessor: "inviteeNickname",
    excelValue: (row: ContractVisitorListModel) => {
      return row.inviteeNickname || "-";
    },
  },
  {
    Header: "초대자 휴대폰 번호",
    accessor: "inviteePhoneNumber",
    excelValue: (row: ContractVisitorListModel) => {
      const phone = row.inviteePhoneNumber;
      return phone ? formatPhoneNumber(phone) || phone : "-";
    },
  },
  {
    Header: "계약명",
    accessor: "contractName",
    excelValue: (row: ContractVisitorListModel) => {
      return row.contractName || "-";
    },
  },
];

export const mobileColumns = [
  {
    Header: "방문신청번호",
    accessor: "visitApplyNumber",
    width: 120,
    sticky: "left",
    Cell: ({ row, value }: Cell<ContractVisitorListModel>) => {
      let detailPath = `${PagePath.visitor.mobileDetail.replace(":contractApplyNumber", `${row.original.contractApplyNumber}`)}`;
      detailPath = `${detailPath.replace(":visitApplyNumber", `${row.original.visitApplyNumber}`)}`;
      return (
        <DetailLink to={detailPath} className="text-underline">
          {value}
        </DetailLink>
      );
    },
  },
  {
    Header: "방문자 ID",
    accessor: "contractVisitorId",
    width: 120,
    Cell: (props: Cell<ContractVisitorListModel>) => {
      return <div className="">{props.value ?? "-"}</div>;
    },
  },
  {
    Header: "이용자 승인상태",
    accessor: "memberApprovalStatus",
    width: 80,
    Cell: ({ value }: Cell<ContractVisitorListModel>) => {
      return <div className="flex-center-center w-100">{value ? <ApprovalStatusTag value={value} /> : "-"}</div>;
    },
  },
  {
    Header: "이용자 마스터 승인상태",
    accessor: "tenantManagerApprovalStatus",
    width: 80,
    Cell: ({ value }: Cell<ContractVisitorListModel>) => {
      return <div className="flex-center-center w-100">{value ? <ApprovalStatusTag value={value} /> : "-"}</div>;
    },
  },
  {
    Header: "관리자 승인상태",
    accessor: "approvalStatus",
    sticky: "left",
    width: 80,
    Cell: ({ value }: Cell<ContractVisitorListModel>) => {
      return <div className="flex-center-center w-100">{value ? <ApprovalStatusTag value={value} /> : "-"}</div>;
    },
  },
  {
    Header: "방문상태",
    accessor: "visitStatus",
    width: 160,
    Cell: ({ value, row }: Cell<ContractVisitorListModel>) => {
      return (
        <div className="flex-center-center w-100 gap10">
          {row.original.isDeleted ? <VisitStatusTag value="VISIT_DELETE" /> : <VisitStatusTag value={value || "-"} />}
          <VisitStatusTagV2 visitStatus={value} accessUpdatedAt={row.original.accessUpdatedAt} isDeleted={row.original.isDeleted} />
        </div>
      );
    },
  },
  {
    Header: "방문자명",
    accessor: "visitorName",
    width: 200,
    Cell: (props: Cell<ContractVisitorListModel>) => {
      return (
        <div className="flex-center-start w-100">
          <BaseTooltip contents={props.value || "-"} isSingleLine={true} />
        </div>
      );
    },
  },
  {
    Header: "방문자 휴대폰 번호",
    accessor: "visitorMobileNumber",
    width: 120,
    Cell: ({ value }: Cell<ContractVisitorListModel>) => {
      const phoneNumber = formatVisitorMobileNumber(value);
      return <div className="flex-center-start w-100">{phoneNumber || "-"}</div>;
    },
  },
  {
    Header: "방문자 이메일",
    accessor: "visitorEmail",
    width: 160,
    Cell: ({ row, value }: Cell<ContractVisitorListModel>) => {
      if (row.original.visitorEmail) {
        return (
          <BaseTooltip
            contents={`${row.original.visitorEmail} ${row.original.visitorEmailLang === "en" ? "(영문)" : "(국문)"}`}
            isSingleLine={true}
          />
        );
      }

      return <div className="flex-center-start w-100">{"-"}</div>;
    },
  },
  {
    Header: "방문자 소속회사",
    accessor: "organizationName",
    width: 160,
    Cell: ({ value }: Cell<ContractVisitorListModel>) => {
      if (value) {
        return (
          <div className="flex-center-start w-100">
            <BaseTooltip contents={value} isSingleLine={true} />
          </div>
        );
      }
      return <div className="flex-center-start w-100">{"-"}</div>;
    },
  },
  {
    Header: "방문 목적",
    accessor: "visitPurpose",
    width: 140,
    Cell: ({ value, row }: Cell<ContractVisitorListModel>) => {
      const display = formatVisitPurpose(value, row.original.visitPurposeDescription);
      if (display === "-") {
        return <div className="flex-center-center w-100">{"-"}</div>;
      }
      if (value === "ETC") {
        return (
          <div className="flex-center-center w-100">
            <BaseTooltip contents={display} className="text-center w-100" />
          </div>
        );
      }
      return <div className="flex-center-center w-100">{display}</div>;
    },
  },
  {
    Header: "방문 시작일시",
    accessor: "visitStartTime",
    width: 160,
    Cell: (props: Cell<ContractVisitorListModel>) => <span>{props.value ? moment(props.value).format(ViewYmdFormat.FULL) : "-"}</span>,
  },
  {
    Header: "방문 종료일시",
    accessor: "visitEndTime",
    width: 160,
    Cell: (props: Cell<ContractVisitorListModel>) => <span>{props.value ? moment(props.value).format(ViewYmdFormat.FULL) : "-"}</span>,
  },

  {
    Header: "초대자",
    accessor: "inviteeMemberId",
    width: 160,
    Cell: (props: Cell<ContractVisitorListModel>) => {
      return (
        <div className="flex-center-start w-100">
          <BaseTooltip contents={props.value || "-"} />
        </div>
      );
    },
  },
  {
    // 신청번호(난수) 대체 컬럼
    Header: "계약관리 ID",
    accessor: "contractManageId",
    width: 120,
    Cell: ({ data, row }: any) => (
      <ContractManageIdLinkCell
        contractManageId={row.original.contractManageId}
        contractManageIdsInPage={data.map((item: ContractVisitorListModel) => item.contractManageId)}
      />
    ),
  },
];
